import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import BadgePill from './BadgePill';
import Pagination from 'components/UI/Pagination';
import ProductBadge from 'data/ProductBadge';
import { getBadgeColorLabel } from 'constants/productBadges';
import { defaultPaginationSelectOptions } from 'constants/paginationOptions';
import { applyPaginationFilter, calculatePages } from 'utils/grid';
import { Background, Border, Text } from 'styles/themeColors';
import { BorderRadius } from 'styles/themeBorderRadius';
import { FontWeight } from 'lib/styles';

const SectionTitle = styled.h3`
  margin: 32px 0 16px 0;
  font-weight: ${FontWeight.medium};
  scroll-margin-top: 96px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid ${Border.light};
  border-radius: ${BorderRadius.s};
  overflow: hidden;
`;

const HeaderCell = styled.th`
  text-align: left;
  padding: 12px 16px;
  background-color: ${Background.shaded};
  border-bottom: 1px solid ${Border.light};
  font-size: 14px;
  font-weight: ${FontWeight.bold};
  color: ${Text.primary};
`;

const Cell = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid ${Border.light};
  font-size: 14px;
  color: ${Text.primary};
  vertical-align: middle;
tr:last-child & {
    border-bottom: none;
  }
`;

const PaginationWrapper = styled.div`
  margin-top: 8px;
`;

const EmptyCell = styled(Cell)`
  color: ${Text.secondary};
  text-align: center;
`;

interface Props {
  title: string;
  badges: ProductBadge[];
  lastColumnHeader: string;
  renderLastColumn: (badge: ProductBadge) => React.ReactNode;
  emptyMessage: string;
}

const BadgeSection = ({
  title,
  badges,
  lastColumnHeader,
  renderLastColumn,
  emptyMessage
}: Props) => {
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const sectionRef = useRef<HTMLHeadingElement>(null);
  const renderedPage = useRef(currentPage);

  const totalPages = calculatePages(badges.length, itemsPerPage);
  const pageBadges = applyPaginationFilter(badges, currentPage, itemsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (renderedPage.current === currentPage) return;

    renderedPage.current = currentPage;
    sectionRef.current?.scrollIntoView({ block: 'nearest' });
  }, [currentPage]);

  return (
    <>
      <SectionTitle ref={sectionRef}>{title}</SectionTitle>
      <Table>
        <thead>
          <tr>
            <HeaderCell style={{ width: '35%' }}>Badge</HeaderCell>
            <HeaderCell style={{ width: '35%' }}>Color</HeaderCell>
            <HeaderCell>{lastColumnHeader}</HeaderCell>
          </tr>
        </thead>
        <tbody>
          {badges.length === 0 ? (
            <tr>
              <EmptyCell colSpan={3}>{emptyMessage}</EmptyCell>
            </tr>
          ) : (
            pageBadges.map(badge => (
              <tr key={badge.id}>
                <Cell>
                  <BadgePill
                    name={badge.name}
                    colorHash={badge.colorHash}
                    isActive={badge.isActive}
                  />
                </Cell>
                <Cell>{getBadgeColorLabel(badge.colorHash)}</Cell>
                <Cell>{renderLastColumn(badge)}</Cell>
              </tr>
            ))
          )}
        </tbody>
      </Table>
      <PaginationWrapper>
        <Pagination
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          selectorOptions={defaultPaginationSelectOptions}
          baseRoute=""
          shouldRedirect={false}
          shouldHandleOnSizeChange
        />
      </PaginationWrapper>
    </>
  );
};

export default BadgeSection;